import React from 'react';
import * as echarts from 'echarts/lib/echarts';
import { get, debounce } from 'lodash';
import ResizeObserver from 'resize-observer-polyfill';

interface ChartSize {
  width: number;
  overlayRange: number;
}

const getChartSize = (instance: echarts.ECharts, len: number) => {
  const option = instance.getOption();
  const left = get(option, ['grid', 0, 'left'], 0);
  const right = get(option, ['grid', 0, 'right'], 0);
  const width = instance.getWidth() - left - right;
  return {
    width,
    overlayRange: Math.ceil(width / len)
  };
}

export default (dateRange: number[]): [null | ChartSize, React.RefObject<HTMLDivElement>] => {
  const len = dateRange.length;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = React.useState<null | ChartSize>(null);
  const handleResize = React.useCallback(() => {
    if (!containerRef.current) {
      return;
    }
    const eventInstance = echarts.getInstanceByDom(containerRef.current);
    if (eventInstance) {
      debounce(() => {
        setChartSize(getChartSize(eventInstance, len))
      }, 100);
    }
  }, [containerRef.current, len]);
  React.useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const eventInstance = echarts.getInstanceByDom(containerRef.current);
    if (!eventInstance) {
      return;
    }
    setChartSize(getChartSize(eventInstance, len));
    return () => {
      setChartSize(null);
    }
  }, [containerRef.current, len]);

  React.useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    // Auto resize
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(containerRef.current);
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, [containerRef.current, handleResize]);

  return [chartSize, containerRef];
};
